import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ArrowPathIcon } from "@heroicons/react/24/outline";
import { getTPRMPlatformDashboardApi } from "../../../services/axios/AuthApis/TPRMApis";
import DataTable from "../../common/DataTable";
import PageLoader from "../../ui/PageLoader";

const METRICS = [
  { key: "customer_count", label: "Customers represented" },
  { key: "total_vendors", label: "Total vendors" },
  { key: "awaiting_vendor_response", label: "Awaiting vendor response" },
  { key: "awaiting_internal_review", label: "Awaiting internal review" },
  { key: "open_high_critical_findings", label: "Open high / critical findings" },
  { key: "overdue_remediation", label: "Overdue remediation" },
];

const showMapping = (row) =>
  row.customer_name || "Unverified / unavailable";

const PlatformDashboardPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setData(await getTPRMPlatformDashboardApi());
    } catch (requestError) {
      setError(
        requestError.response?.data?.detail ||
          requestError.message ||
          "Unable to load the TPRM portfolio.",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const customerColumns = useMemo(
    () => [
      {
        accessorKey: "customer_name",
        header: "Customer",
        accessorFn: showMapping,
        meta: {
          filter: {
            variant: "text",
            label: "Customer",
            placeholder: "Filter customer",
          },
        },
        cell: ({ row }) => (
          <div>
            <span>{showMapping(row.original)}</span>
            {row.original.customer_mapping_status !== "verified" && (
              <span className="ml-2 text-xs text-amber-700">
                {row.original.customer_mapping_status}
              </span>
            )}
          </div>
        ),
      },
      {
        accessorKey: "vendor_count",
        header: "Vendors",
        meta: {
          filter: {
            variant: "number",
            label: "Vendor count",
            placeholder: "Exact count",
          },
        },
      },
      {
        accessorKey: "assessment_coverage_percent",
        header: "Assessment coverage",
        meta: {
          filter: {
            variant: "number",
            label: "Assessment coverage",
            placeholder: "Exact percent",
          },
        },
        cell: ({ row, getValue }) =>
          getValue() == null
            ? "Unavailable (no vendors)"
            : `${getValue()}% (${row.original.assessed_vendor_count}/${row.original.vendor_count})`,
      },
      {
        accessorKey: "open_high_risk_findings",
        header: "Open high-risk findings",
        meta: {
          filter: {
            variant: "number",
            label: "Open high-risk findings",
            placeholder: "Exact count",
          },
        },
      },
    ],
    [],
  );

  if (loading && !data) {
    return <PageLoader title="Loading TPRM portfolio..." minH="min-h-[40vh]" />;
  }

  if (error && !data) {
    return (
      <section className="rounded-xl border border-rose-200 bg-white p-6" role="alert">
        <h2 className="font-semibold text-slate-900">
          TPRM portfolio could not be loaded
        </h2>
        <p className="mt-2 text-sm text-slate-600">{error}</p>
        <button
          type="button"
          onClick={loadDashboard}
          className="mt-4 inline-flex items-center gap-2 rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium"
        >
          <ArrowPathIcon className="h-4 w-4" />
          Try again
        </button>
      </section>
    );
  }

  const coverage = data?.assessment_coverage || {};
  const riskDistribution = data?.vendor_risk_distribution || {};

  return (
    <section aria-labelledby="tprm-platform-dashboard-heading" className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2
            id="tprm-platform-dashboard-heading"
            className="text-xl font-semibold text-slate-900 dark:text-white"
          >
            Cross-customer TPRM dashboard
          </h2>
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
            Portfolio metrics aggregated from persisted tenant-owned TPRM records.
          </p>
        </div>
        <button
          type="button"
          onClick={loadDashboard}
          disabled={loading}
          className="inline-flex items-center gap-2 rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 disabled:opacity-50"
        >
          <ArrowPathIcon className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>

      {error && (
        <p className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700" role="alert">
          {error}
        </p>
      )}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {METRICS.map(({ key, label }) => (
          <article
            key={key}
            className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900"
          >
            <p className="text-sm font-medium text-slate-500">{label}</p>
            <p className="mt-2 text-2xl font-semibold text-slate-900 dark:text-white">
              {data?.[key] ?? "—"}
            </p>
          </article>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <article className="rounded-xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
          <h3 className="font-semibold text-slate-900 dark:text-white">
            Vendor distribution by assessment risk tier
          </h3>
          <p className="mt-1 text-xs text-slate-500">
            Derived from persisted vendor risk inputs and completed assessment scores.
          </p>
          <dl className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-5">
            {[
              ["Critical", "critical"],
              ["High", "high"],
              ["Medium", "medium"],
              ["Low", "low"],
              ["Unassessed", "unassessed"],
            ].map(([label, key]) => (
              <div key={key} className="rounded-lg bg-slate-50 p-3 dark:bg-slate-800">
                <dt className="text-xs text-slate-500">{label}</dt>
                <dd className="mt-1 text-lg font-semibold text-slate-900 dark:text-white">
                  {riskDistribution[key] ?? "—"}
                </dd>
              </div>
            ))}
          </dl>
        </article>

        <article className="rounded-xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
          <h3 className="font-semibold text-slate-900 dark:text-white">
            Assessment coverage
          </h3>
          <p className="mt-1 text-xs text-slate-500">
            Denominator: all TPRM vendors with a verified tenant ID. Numerator:
            distinct vendors with at least one completed assessment.
          </p>
          <p className="mt-4 text-3xl font-semibold text-slate-900 dark:text-white">
            {coverage.percentage == null ? "Unavailable" : `${coverage.percentage}%`}
          </p>
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
            {coverage.assessed_vendors ?? "—"} / {coverage.total_vendors ?? "—"} vendors
          </p>
        </article>
      </div>

      <article className="rounded-xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
        <h3 className="mb-1 font-semibold text-slate-900 dark:text-white">
          Customer comparison
        </h3>
        <p className="mb-4 text-xs text-slate-500">
          Filters and sorting apply to the complete comparison dataset returned by this
          portfolio endpoint. Unverified company mappings are not inferred.
        </p>
        <DataTable
          columns={customerColumns}
          data={data?.customers || []}
          enableColumnFilters
          enableGlobalFilter={false}
          enableColumnVisibility={false}
          initialPageSize={10}
          emptyMessage="No customer portfolio records are available."
          className="[&_thead]:text-slate-700 [&_th]:text-xs [&_tbody_td]:text-sm"
        />
      </article>

      <article className="rounded-xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
        <h3 className="mb-3 font-semibold text-slate-900 dark:text-white">
          Recent cross-customer TPRM activity
        </h3>
        {data?.recent_activity?.length ? (
          <ul className="divide-y divide-slate-100 dark:divide-slate-800">
            {data.recent_activity.map((activity, index) => (
              <li
                key={`${activity.created_at}-${activity.action}-${index}`}
                className="flex flex-wrap justify-between gap-2 py-3 text-sm"
              >
                <span className="font-medium text-slate-800 dark:text-slate-200">
                  {activity.action} · {activity.resource_type}
                </span>
                <span className="text-slate-500">
                  {showMapping(activity)} ·{" "}
                  {activity.created_at
                    ? new Date(activity.created_at).toLocaleString()
                    : "Date unavailable"}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-slate-500">No audit activity is available.</p>
        )}
      </article>
    </section>
  );
};

export default PlatformDashboardPage;
